import type { AxiosError } from 'axios';
import { Button, Container, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import type { Contract, Space, WorkPackage } from '@/api/generated/models';
import type { UpdateWorkPackageRequestStatus } from '@/api/generated/models/updateWorkPackageRequestStatus';
import { useGetContract, useListContracts } from '@/api/generated/contracts/contracts';
import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import {
  getGetWorkPackageQueryKey,
  getListAllWorkPackagesQueryKey,
  useCreateWorkPackage,
  useGetSpace,
  useGetWorkPackage,
  useListCoveredSpaces,
  useUpdateWorkPackage
} from '@/api/generated/work-structure/work-structure';
import {
  CardSection,
  DateField,
  FormActions,
  MutationButton,
  PageHeading,
  SelectField,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';
import { buildSpaceOptions, statusOptions } from '@/features/work-packages/workPackageDisplay';

const schema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  status: z.string(),
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional()
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  code: '',
  name: '',
  description: '',
  status: WorkPackageStatus.DRAFT,
  validFrom: null,
  validTo: null
};

const errorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

export const WorkPackageFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: workPackageData } = useGetWorkPackage(id ?? '', { query: { enabled: isEdit } });
  const workPackage = workPackageData as WorkPackage | undefined;

  // Lokacija (ugovor→prostor) se bira SAMO pri kreiranju — radni paket je
  // nakon toga trajno vezan na taj prostor (vidi WorkStructureService).
  const [contractId, setContractId] = useState<string | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);

  // refetchOnMount 'always' — ugovor je možda upravo poslan/prihvaćen (druga strana, drugi tab);
  // keš star do 5 min bi ga prikazao bez prostora pa odabir prostora ostaje prazan.
  const { data: contractsData } = useListContracts({ query: { refetchOnMount: 'always' } });
  const contracts: Contract[] = contractsData ?? [];

  // Samo prostori koje ugovor stvarno obuhvaća (Razrada prostora /
  // ContractSpaceSelectionModal) — ne cijelo stablo prostora objekta, inače
  // backend odbija spremanje porukom "Prostor nije dio odabranog seta
  // prostora ovog ugovora" tek NAKON što korisnik popuni cijeli obrazac.
  // staleTime 0, ne refetchOnMount: upit se uključuje tek kad se odabere ugovor, a refetchOnMount
  // vrijedi samo pri mountu komponente — svjež keš (do 5 min) se inače ne bi ponovno dohvatio.
  const { data: coveredSpacesData } = useListCoveredSpaces(contractId ?? '', {
    query: { enabled: !!contractId, staleTime: 0 }
  });
  const spaces: Space[] = (coveredSpacesData as Space[] | undefined) ?? [];
  const spaceOptions = buildSpaceOptions(spaces);

  // Edit mode: lokacija se ne mijenja, samo se prikazuje read-only (izravno
  // preko workPackage.contractId/spaceId) da se vidi na koji je ugovor i
  // prostor radni paket vezan.
  const { data: currentContractData } = useGetContract(workPackage?.contractId ?? '', {
    query: { enabled: isEdit && !!workPackage?.contractId }
  });
  const currentContract = currentContractData as Contract | undefined;

  const { data: currentSpaceData } = useGetSpace(workPackage?.spaceId ?? '', {
    query: { enabled: isEdit && !!workPackage?.spaceId }
  });
  const currentSpace = currentSpaceData as Space | undefined;

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (isEdit && workPackage) {
      form.setValues({
        code: workPackage.code ?? '',
        name: workPackage.name ?? '',
        description: workPackage.description ?? '',
        status: workPackage.status ?? WorkPackageStatus.DRAFT,
        validFrom: workPackage.validFrom ?? null,
        validTo: workPackage.validTo ?? null
      });
    }
  }, [isEdit, workPackage]);

  const createMutation = useCreateWorkPackage({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListAllWorkPackagesQueryKey() });
        const createdId = (created as WorkPackage | undefined)?.id;
        const savedMessage = t('workPackages.form.createSuccess');
        if (createdId) navigate(`/work-packages/${createdId}`, { state: { savedMessage } });
        else navigate('/work-packages', { state: { savedMessage } });
      }
    }
  });

  const updateMutation = useUpdateWorkPackage({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListAllWorkPackagesQueryKey() });
        if (id) queryClient.invalidateQueries({ queryKey: getGetWorkPackageQueryKey(id) });
        navigate(`/work-packages/${id}`, {
          state: { savedMessage: t('workPackages.form.updateSuccess') }
        });
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && id && workPackage) {
      updateMutation.mutate({
        id,
        data: {
          code: values.code,
          name: values.name,
          description: values.description,
          status: values.status as UpdateWorkPackageRequestStatus,
          validFrom: values.validFrom ?? undefined,
          validTo: values.validTo ?? undefined,
          sortOrder: workPackage.sortOrder
        }
      });
    } else if (contractId && spaceId) {
      createMutation.mutate({
        contractId,
        data: {
          spaceId,
          code: values.code,
          name: values.name,
          description: values.description,
          validFrom: values.validFrom ?? undefined,
          validTo: values.validTo ?? undefined
        }
      });
    }
  };

  return (
    <Container size="md" px={0}>
      <Stack gap={18}>
        <PageHeading
          title={isEdit ? t('workPackages.form.editTitle') : t('workPackages.form.newTitle')}
        />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap={18}>
            <CardSection title={t('workPackages.detail.basicInfoTitle')}>
              <Stack gap={16} p={20}>
                {mutation.isError && (
                  <Toast tone="danger">{errorMessage(mutation.error, t('common.error'))}</Toast>
                )}

                <SelectField
                  label={t('workPackages.form.fieldContract')}
                  placeholder={t('workPackages.form.contractPlaceholder')}
                  required={!isEdit}
                  disabled={isEdit}
                  data={
                    isEdit
                      ? currentContract?.id
                        ? [
                            {
                              value: currentContract.id,
                              label: currentContract.contractNumber ?? currentContract.name ?? ''
                            }
                          ]
                        : []
                      : contracts
                          .filter((c) => c.id)
                          .map((c) => ({
                            value: c.id as string,
                            label: c.contractNumber ?? c.name ?? ''
                          }))
                  }
                  value={isEdit ? (currentContract?.id ?? null) : contractId}
                  onChange={
                    isEdit
                      ? () => {}
                      : (value) => {
                          setContractId(value);
                          setSpaceId(null);
                        }
                  }
                />

                <div className="form-row-2col">
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('workPackages.form.fieldCode')}
                      required
                      {...form.getInputProps('code')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <TextField
                      label={t('workPackages.form.fieldName')}
                      required
                      {...form.getInputProps('name')}
                    />
                  </div>
                </div>

                <TextareaField
                  label={t('workPackages.form.fieldDescription')}
                  minRows={3}
                  {...form.getInputProps('description')}
                />

                <div className="form-row-2col">
                  {isEdit && (
                    <div style={{ flex: 1 }}>
                      <SelectField
                        label={t('workPackages.form.fieldStatus')}
                        required
                        allowDeselect={false}
                        data={statusOptions.map((option) => ({
                          value: option.value,
                          label: t(option.labelKey)
                        }))}
                        {...form.getInputProps('status')}
                      />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <DateField
                      label={t('workPackages.form.fieldValidFrom')}
                      valueFormat="DD.MM.YYYY."
                      clearable
                      {...form.getInputProps('validFrom')}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DateField
                      label={t('workPackages.form.fieldValidTo')}
                      valueFormat="DD.MM.YYYY."
                      clearable
                      {...form.getInputProps('validTo')}
                    />
                  </div>
                </div>
              </Stack>
            </CardSection>

            <CardSection title={t('workPackages.detail.locationTitle')}>
              <Stack gap={16} p={20}>
                <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                  {t('workPackages.form.locationIntro')}
                </Text>
                <div>
                  <SelectField
                    label={t('workPackages.form.fieldSpace')}
                    placeholder={t('workPackages.form.spacePlaceholder')}
                    required={!isEdit}
                    disabled={isEdit || !contractId}
                    data={
                      isEdit
                        ? currentSpace?.id
                          ? [{ value: currentSpace.id, label: currentSpace.name ?? '' }]
                          : []
                        : spaceOptions
                    }
                    value={isEdit ? (currentSpace?.id ?? null) : spaceId}
                    onChange={isEdit ? () => {} : setSpaceId}
                  />
                </div>
              </Stack>
            </CardSection>

            <FormActions>
              <Button
                variant="outline"
                onClick={() => navigate(isEdit ? `/work-packages/${id}` : '/work-packages')}
              >
                {t('common.cancel')}
              </Button>
              <MutationButton
                type="submit"
                isPending={mutation.isPending}
                isPaused={mutation.isPaused}
                disabled={!isEdit && !spaceId}
              >
                {isEdit ? t('workPackages.form.saveChanges') : t('workPackages.form.save')}
              </MutationButton>
            </FormActions>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
};
